import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTicker } from '../../context/TickerContext';

interface TickerProps {
    text?: string;
    speed?: number;
    isVisible?: boolean;
    pageType?: keyof Omit<typeof TICKER_MESSAGES, 'ENABLED'>;
}

export interface TickerPageConfig {
    enabled: boolean;
    messages: string[];
    rotation?: 'random' | 'sequential' | 'fixed'; // How to rotate through multiple messages
}

type TickerConfigType = {
    ENABLED: boolean;
    DISCOVER: TickerPageConfig;
    OWNPROFILE: TickerPageConfig;
    LIKED: TickerPageConfig;
    SEARCH: TickerPageConfig;
    DEFAULT: TickerPageConfig;
}

export const TICKER_MESSAGES: TickerConfigType = {
    ENABLED: true, // Master switch to disable all ticker messages globally

    // Page-specific configurations with multiple possible messages
    // Rotation types:
    // - 'fixed': Always shows the first message in the array (default)
    // - 'random': Randomly selects a message on page load
    // - 'sequential': Cycles through messages in order on page load
    DISCOVER: {
        enabled: true,
        messages: [
            '✨ Improved algorithm helps you discover the right collaborators based on skills, headline, artist, genres, location and more! Take advantage with a great profile to find opportunities with better matching!',
            '💖 Like someone by pressing the heart button on their profile to start connecting (find them later on the Liked page)...',
            '🔍 Discover new collaborators by searching for skills, genres, locations, etc...'
        ],
        rotation: 'sequential'
    },

    OWNPROFILE: {
        enabled: true,
        messages: [
            '👤 Fill your skills, headline, artist, genres, and location to match with the right collaborators! Make your profile great to find opportunities with better matching!',
            '🌟 Did you know? Profiles with awesome highlights get 10x more visits!'
        ],
        rotation: 'sequential'
    },

    LIKED: {
        enabled: true,
        messages: [
            '💞 Your liked profiles are waiting! Connect with them and start making music together...'
        ],
        rotation: 'fixed'
    },

    SEARCH: {
        enabled: true,
        messages: [
            '🔎 Search through our talented community of musicians to find the best opportunities for you...'
        ],
        rotation: 'fixed'
    },

    DEFAULT: {
        enabled: true,
        messages: [
            '🎵 Welcome to JamWith - Where the music community connects, collabs, and showcases their stuff!'
        ],
        rotation: 'fixed'
    }
};

// Helper functions for ticker messages
export const getTickerMessage = (pageType: keyof Omit<TickerConfigType, 'ENABLED'>): string | null => {
    if (!TICKER_MESSAGES.ENABLED) return null;

    // Validate page type - default to DEFAULT if not a valid page type
    if (!pageType || !TICKER_MESSAGES[pageType]) {
        console.warn(`Invalid page type for ticker: ${String(pageType)}, using DEFAULT instead`);
        pageType = 'DEFAULT';
    }

    const pageConfig = TICKER_MESSAGES[pageType];
    if (!pageConfig || !pageConfig.enabled) return null;

    const { messages, rotation = 'fixed' } = pageConfig;
    if (!messages || messages.length === 0) {
        console.warn(`No messages defined for page type: ${pageType}`);
        return null;
    }

    let selectedMessage: string;

    // Select message based on rotation type
    if (rotation === 'fixed' || messages.length === 1) {
        selectedMessage = messages[0];
    } else if (rotation === 'random') {
        const randomIndex = Math.floor(Math.random() * messages.length);
        selectedMessage = messages[randomIndex];
    } else if (rotation === 'sequential') {
        // Use session storage to keep track of which message was last shown
        const storageKey = `ticker-${pageType}-index`;
        const lastIndex = parseInt(sessionStorage.getItem(storageKey) || '0');
        const nextIndex = (lastIndex + 1) % messages.length;
        sessionStorage.setItem(storageKey, nextIndex.toString());
        selectedMessage = messages[nextIndex];
    } else {
        selectedMessage = messages[0];
    }

    return selectedMessage;
};

const TICKER_VISIBILITY_KEY = 'ticker-visibility';
const BUTTON_VISIBILITY_KEY = 'ticker-button-visibility';
const SHOW_BUTTON_TIMEOUT = 5000; // 5 seconds
const HIDE_ANIMATION_DURATION = 1500; // 1.5 seconds for fade-out animation
const SHOW_ANIMATION_DURATION = 300; // 0.3 seconds for fade-in animation
const BOTTOM_THRESHOLD = 80; // Bottom area height in pixels
const RIGHT_THRESHOLD = 80; // Right area width in pixels
const SCROLLBAR_WIDTH = 20; // Estimated scrollbar width to avoid
const TEXT_TRANSITION_DURATION = 800; // Text transition duration in ms
const TICKER_RESERVE_MS = 300; // Keep in sync with --ticker-reserve in index.css
const TICKER_NIGHT_GRADIENT = 'dark:from-jw-chrome dark:via-jw-chrome dark:to-jw-chrome';

const Ticker: React.FC<TickerProps> = ({ text, speed = 1, isVisible = true, pageType = 'DEFAULT' }) => {
    const { text: contextText, setText: setContextText } = useTicker();
    const tickerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<HTMLDivElement>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    // Store a fallback message to use in case of empty text
    const [fallbackMessage, setFallbackMessage] = useState<string>(() => {
        return getTickerMessage(pageType) || TICKER_MESSAGES.DEFAULT.messages[0] || 'Welcome to JamWith!';
    });
    const [currentText, setCurrentText] = useState<string>(() => {
        // Initialize with the provided text, context text, or a page-specific message
        if (typeof text === 'string' && text.trim()) return text;
        if (typeof contextText === 'string' && contextText.trim()) return contextText;
        const pageMsg = getTickerMessage(pageType);
        return typeof pageMsg === 'string' && pageMsg.trim() ? pageMsg : fallbackMessage;
    });
    const [isFading, setIsFading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showTicker, setShowTicker] = useState(() => {
        // Initialize from sessionStorage, default to true if not set
        const savedVisibility = sessionStorage.getItem(TICKER_VISIBILITY_KEY);
        return savedVisibility ? savedVisibility === 'true' : true;
    });
    const [showButton, setShowButton] = useState(() => {
        // Only show the button by default if ticker is hidden
        const savedTickerVisibility = sessionStorage.getItem(TICKER_VISIBILITY_KEY);
        const tickerVisible = savedTickerVisibility ? savedTickerVisibility === 'true' : true;

        if (tickerVisible) {
            return false; // No need to show button if ticker is visible
        }

        // Check if we have a saved button visibility state
        const savedButtonVisibility = sessionStorage.getItem(BUTTON_VISIBILITY_KEY);
        return savedButtonVisibility ? savedButtonVisibility === 'true' : true;
    });
    const [buttonIsAnimatingOut, setButtonIsAnimatingOut] = useState(false);
    const [animationCompleted, setAnimationCompleted] = useState(false);
    const buttonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const textTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hideTickerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reservePxRef = useRef<string | null>(null);
    // The band stays mounted through the height transition, then drops the marquee.
    const bandOpen = Boolean(isVisible && showTicker && TICKER_MESSAGES.ENABLED);
    const [holdBar, setHoldBar] = useState(false);
    const barMounted = bandOpen || holdBar;

    useLayoutEffect(() => {
        const root = document.documentElement;
        if (bandOpen) setHoldBar(true);

        const nextReserve = bandOpen ? 'var(--ticker-full)' : '0px';
        if (reservePxRef.current !== nextReserve) {
            reservePxRef.current = nextReserve;
            root.style.setProperty('--ticker-reserve', nextReserve);
        }

        let frame = 0;
        if (!root.classList.contains('ticker-ready')) {
            frame = requestAnimationFrame(() => root.classList.add('ticker-ready'));
        }
        return () => cancelAnimationFrame(frame);
    }, [bandOpen]);

    useEffect(() => {
        if (bandOpen || !holdBar) return undefined;

        const root = document.documentElement;
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            setHoldBar(false);
        };
        const onEnd = (event: TransitionEvent) => {
            if (event.target === root && event.propertyName === '--ticker-reserve') finish();
        };
        const ready = root.classList.contains('ticker-ready');
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const wait = !ready || reduce ? 0 : TICKER_RESERVE_MS + 80;

        root.addEventListener('transitionend', onEnd);
        const timeout = window.setTimeout(finish, wait);
        return () => {
            settled = true;
            root.removeEventListener('transitionend', onEnd);
            window.clearTimeout(timeout);
        };
    }, [bandOpen, holdBar]);

    // Early return if disabled globally
    if (!TICKER_MESSAGES.ENABLED) {
        return null;
    }

    // Get message for current page type when it changes
    useEffect(() => {
        if (typeof text === 'string') {
            // If explicit text is provided, use that
            setContextText(text);
            setCurrentText(text);
            setIsFading(false);
        } else {
            // Otherwise, get page-specific message
            const pageMessage = getTickerMessage(pageType);
            if (typeof pageMessage === 'string' && pageMessage !== currentText) {
                setContextText(pageMessage);

                // Use fade transition for page changes
                setIsFading(true);
                setTimeout(() => {
                    setCurrentText(pageMessage);
                    setTimeout(() => {
                        setIsFading(false);
                    }, 100);
                }, 300);
            }
        }
    }, [pageType, text]);

    // Set CSS variables for animation durations
    useEffect(() => {
        document.documentElement.style.setProperty(
            '--ticker-show-animation-duration',
            `${SHOW_ANIMATION_DURATION / 1000}s`
        );
        document.documentElement.style.setProperty(
            '--ticker-hide-animation-duration',
            `${HIDE_ANIMATION_DURATION / 1000}s`
        );
        document.documentElement.style.setProperty(
            '--ticker-text-transition',
            `${TEXT_TRANSITION_DURATION / 1000}s`
        );
    }, []);

    // Reset button visibility and timeout when ticker is hidden
    useEffect(() => {
        if (!showTicker) {
            // Don't automatically show button when ticker is hidden
            // Let the button stay in its current state
            // This allows the button to remain hidden across page navigation

            // If button is visible, set the timeout to hide it
            if (showButton) {
                // Clear any existing timeouts
                if (buttonTimeoutRef.current) {
                    clearTimeout(buttonTimeoutRef.current);
                }
                if (animationTimeoutRef.current) {
                    clearTimeout(animationTimeoutRef.current);
                }

                // Set new timeout to start fade-out animation
                buttonTimeoutRef.current = setTimeout(() => {
                    setButtonIsAnimatingOut(true);

                    // Set timeout to actually hide the button after animation completes
                    animationTimeoutRef.current = setTimeout(() => {
                        setShowButton(false);
                        setButtonIsAnimatingOut(false);
                    }, HIDE_ANIMATION_DURATION);
                }, SHOW_BUTTON_TIMEOUT);
            }
        } else {
            // If ticker is visible, button should be hidden
            setShowButton(false);
            setButtonIsAnimatingOut(false);
        }

        return () => {
            if (buttonTimeoutRef.current) {
                clearTimeout(buttonTimeoutRef.current);
            }
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [showTicker, showButton]);

    // Save ticker visibility to sessionStorage
    useEffect(() => {
        sessionStorage.setItem(TICKER_VISIBILITY_KEY, String(showTicker));
    }, [showTicker]);

    // Save button visibility to sessionStorage
    useEffect(() => {
        sessionStorage.setItem(BUTTON_VISIBILITY_KEY, String(showButton));
    }, [showButton]);

    const toggleVisibility = () => {
        const newVisibility = !showTicker;
        setShowTicker(newVisibility);

        // If we're hiding the ticker, make the show button visible immediately
        if (!newVisibility) {
            // Cancel any existing timeouts
            if (buttonTimeoutRef.current) {
                clearTimeout(buttonTimeoutRef.current);
            }
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }

            // Show the button immediately
            setButtonIsAnimatingOut(false);
            setShowButton(true);

            // Start the auto-hide timer
            buttonTimeoutRef.current = setTimeout(() => {
                setButtonIsAnimatingOut(true);

                // Set timeout to actually hide the button after animation completes
                animationTimeoutRef.current = setTimeout(() => {
                    setShowButton(false);
                    setButtonIsAnimatingOut(false);
                }, HIDE_ANIMATION_DURATION);
            }, SHOW_BUTTON_TIMEOUT);
        }
    };

    // Check if an interaction is in the bottom-right corner zone (excluding scrollbar)
    const isInteractionInButtonZone = (x: number, y: number) => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Define bottom-right corner zone
        const isInBottomRightZone =
            y >= windowHeight - BOTTOM_THRESHOLD &&
            x >= windowWidth - RIGHT_THRESHOLD;

        // Exclude scrollbar area
        const isInScrollbarArea = x >= windowWidth - SCROLLBAR_WIDTH;

        // Return true if in zone but not in scrollbar
        return isInBottomRightZone && !isInScrollbarArea;
    };

    // Show button if interaction happens in the button zone
    const showButtonIfInZone = (x: number, y: number) => {
        if (!showTicker && isInteractionInButtonZone(x, y) && (!showButton || buttonIsAnimatingOut)) {
            // Cancel any existing animation and timeouts
            if (buttonTimeoutRef.current) {
                clearTimeout(buttonTimeoutRef.current);
            }
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }

            setButtonIsAnimatingOut(false);
            setShowButton(true);

            // Set new timeout for fade-out
            buttonTimeoutRef.current = setTimeout(() => {
                setButtonIsAnimatingOut(true);

                // Set timeout to actually hide the button after animation completes
                animationTimeoutRef.current = setTimeout(() => {
                    setShowButton(false);
                    setButtonIsAnimatingOut(false);
                }, HIDE_ANIMATION_DURATION);
            }, SHOW_BUTTON_TIMEOUT);
        }
    };

    // Mouse movement detection to show button again if it's hidden
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            showButtonIfInZone(e.clientX, e.clientY);
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [showTicker, showButton, buttonIsAnimatingOut]);

    // Touch event handling for mobile
    useEffect(() => {
        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length > 0) {
                showButtonIfInZone(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        window.addEventListener('touchstart', handleTouchStart);
        return () => {
            window.removeEventListener('touchstart', handleTouchStart);
        };
    }, [showTicker, showButton, buttonIsAnimatingOut]);

    // Check if we're on a mobile device
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Initial check
        checkIfMobile();

        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Add safe area styles on mount, before paint, so the band height includes them.
    useLayoutEffect(() => {
        // Add CSS variables for safe area insets to the document
        document.documentElement.style.setProperty('--sat', '0px');
        document.documentElement.style.setProperty('--sar', '0px');
        document.documentElement.style.setProperty('--sab', '0px');
        document.documentElement.style.setProperty('--sal', '0px');

        // Try to use env() if available (works on iOS/Safari and Android Chrome)
        if (CSS.supports('padding-bottom: env(safe-area-inset-bottom)')) {
            document.documentElement.style.setProperty('--sat', 'env(safe-area-inset-top)');
            document.documentElement.style.setProperty('--sar', 'env(safe-area-inset-right)');
            document.documentElement.style.setProperty('--sab', 'env(safe-area-inset-bottom)');
            document.documentElement.style.setProperty('--sal', 'env(safe-area-inset-left)');

            // Also set max inset variables for calculations
            document.documentElement.style.setProperty('--safe-area-max-inset-bottom', 'env(safe-area-max-inset-bottom, 36px)');
        } else {
            // Fallback for Android devices that may not support env()
            // Estimate navigation bar height on Android
            const isAndroid = /android/i.test(navigator.userAgent);
            if (isAndroid) {
                document.documentElement.style.setProperty('--sab', '48px'); // Common Android nav bar height
                document.documentElement.style.setProperty('--safe-area-max-inset-bottom', '48px');
            }
        }
    }, []);

    // Update fallback message when page type changes
    useEffect(() => {
        const newFallback = getTickerMessage(pageType) || TICKER_MESSAGES.DEFAULT.messages[0] || 'Welcome to JamWith!';
        setFallbackMessage(newFallback);
    }, [pageType]);

    // Handle page visibility changes to prevent empty text when switching tabs
    useEffect(() => {
        const handleVisibilityChange = () => {
            // Only process visibility changes when ticker is visible
            if (!showTicker || !isVisible) return;

            if (document.visibilityState === 'visible') {
                // Check if text is empty when page becomes visible again
                if (!currentText || !currentText.trim()) {
                    console.warn('Ticker text was empty after visibility change, restoring');
                    // Use fallback message if available, otherwise get a new one
                    const validText = fallbackMessage || getTickerMessage(pageType) ||
                        TICKER_MESSAGES.DEFAULT.messages[0] || 'Welcome to JamWith!';
                    setCurrentText(validText);
                    setContextText(validText);
                    setIsFading(false);
                }
            }
        };

        // Register visibility change event
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentText, pageType, fallbackMessage, showTicker, isVisible]);

    // Handle text transitions with smooth fade effect
    useEffect(() => {
        // Skip all text processing when ticker is hidden
        if (!showTicker || !isVisible) return;

        if (typeof contextText === 'string' && contextText !== currentText && !isFading) {
            // Only update if there's a new message from context and not already fading
            if (textTimeoutRef.current) {
                clearTimeout(textTimeoutRef.current);
                textTimeoutRef.current = null;
            }

            // Skip empty text updates
            if (!contextText.trim()) {
                console.warn('Attempted to update ticker with empty text, skipping update');
                return;
            }

            // Fade out first
            setIsFading(true);

            let fadeInTimeoutId: NodeJS.Timeout;

            // After short delay, update text and fade back in
            textTimeoutRef.current = setTimeout(() => {
                // Store valid text value before state update
                const textToSet = contextText.trim()
                    ? contextText
                    : fallbackMessage;

                setCurrentText(textToSet);

                // Fade back in after text is updated
                fadeInTimeoutId = setTimeout(() => {
                    setIsFading(false);
                }, 100);
            }, 300);

            // Clean up both timeouts if effect runs again before completion
            return () => {
                if (textTimeoutRef.current) {
                    clearTimeout(textTimeoutRef.current);
                    textTimeoutRef.current = null;
                }
                if (fadeInTimeoutId) {
                    clearTimeout(fadeInTimeoutId);
                }
            };
        }
    }, [contextText, currentText, isFading, pageType, fallbackMessage, showTicker, isVisible]);

    // Add additional safety check to periodically verify text is not empty
    useEffect(() => {
        // Don't run periodic checks when hidden
        if (!showTicker || !isVisible) return;

        // Every 10 seconds, check if the text is somehow empty and restore it if needed
        const periodicCheckInterval = setInterval(() => {
            if (!currentText || !currentText.trim()) {
                console.warn('Ticker periodic check found empty text, restoring default message');
                const validText = fallbackMessage || getTickerMessage(pageType) ||
                    TICKER_MESSAGES.DEFAULT.messages[0] || 'Welcome to JamWith!';
                setCurrentText(validText);
                setContextText(validText);
                setIsFading(false);
            }
        }, 10000); // Check more frequently - every 10 seconds

        return () => clearInterval(periodicCheckInterval);
    }, [currentText, pageType, fallbackMessage, showTicker, isVisible]);

    // On initial mount, ensure we're not in a fading state and have valid text
    useEffect(() => {
        setIsFading(false);

        // Ensure we start with valid text
        if (!currentText || !currentText.trim()) {
            const validText = fallbackMessage || getTickerMessage(pageType) ||
                TICKER_MESSAGES.DEFAULT.messages[0] || 'Welcome to JamWith!';
            setCurrentText(validText);
            setContextText(validText);
        }
    }, []);

    // Add a safety check to prevent empty ticker content
    useEffect(() => {
        // Skip when ticker is hidden
        if (!showTicker || !isVisible) return;

        // If text somehow becomes empty after initial render, restore it
        if (isInitialized && (!currentText || !currentText.trim())) {
            console.warn('Ticker text became empty, restoring default message');
            const validText = fallbackMessage || getTickerMessage(pageType) ||
                TICKER_MESSAGES.DEFAULT.messages[0] || 'Welcome to JamWith!';
            setCurrentText(validText);
            setContextText(validText);
        }
    }, [currentText, isInitialized, pageType, showTicker, isVisible]);

    // Handle animation end event to hide ticker
    useEffect(() => {
        if (!isInitialized || animationCompleted) return;

        // Set timeout to hide the ticker after 80 seconds
        if (hideTickerTimeoutRef.current) {
            clearTimeout(hideTickerTimeoutRef.current);
        }

        hideTickerTimeoutRef.current = setTimeout(() => {
            setAnimationCompleted(true);
            setShowTicker(false);
            // Show the button after hiding the ticker
            setButtonIsAnimatingOut(false);
            setShowButton(true);

            // Auto-hide button after some time
            if (buttonTimeoutRef.current) {
                clearTimeout(buttonTimeoutRef.current);
            }
            buttonTimeoutRef.current = setTimeout(() => {
                setButtonIsAnimatingOut(true);
                if (animationTimeoutRef.current) {
                    clearTimeout(animationTimeoutRef.current);
                }
                animationTimeoutRef.current = setTimeout(() => {
                    setShowButton(false);
                    setButtonIsAnimatingOut(false);
                }, HIDE_ANIMATION_DURATION);
            }, SHOW_BUTTON_TIMEOUT);

            // Save ticker visibility to session storage
            sessionStorage.setItem(TICKER_VISIBILITY_KEY, 'false');
        }, 120000); // 120 seconds

        return () => {
            if (hideTickerTimeoutRef.current) {
                clearTimeout(hideTickerTimeoutRef.current);
            }
        };
    }, [isInitialized, animationCompleted]);

    // Reset animation when component is unmounted
    useEffect(() => {
        return () => {
            if (hideTickerTimeoutRef.current) {
                clearTimeout(hideTickerTimeoutRef.current);
            }
            if (buttonTimeoutRef.current) {
                clearTimeout(buttonTimeoutRef.current);
            }
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
            if (textTimeoutRef.current) {
                clearTimeout(textTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        // Wait for a short delay before starting animation to allow page content to load
        const initTimeout = setTimeout(() => {
            setIsInitialized(true);
        }, 500);

        return () => {
            clearTimeout(initTimeout);
        };
    }, []);

    return (
        <>
            {barMounted && (
                <div
                    className={`ticker-band fixed left-0 right-0 z-50 flex items-end overflow-hidden shadow-lg ${bandOpen ? 'cursor-pointer' : ''}`}
                    data-open={bandOpen ? 'true' : 'false'}
                    style={{ bottom: 0, height: 'var(--ticker-reserve)' }}
                    onClick={bandOpen ? toggleVisibility : undefined}
                    title={bandOpen ? 'Click to hide ticker' : undefined}
                    aria-hidden={bandOpen ? undefined : true}
                >
                    <div
                        className={`relative w-full shrink-0 overflow-hidden whitespace-nowrap bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 text-white ${TICKER_NIGHT_GRADIENT}`}
                        style={{
                            height: 'var(--ticker-full)',
                            paddingTop: '0.125rem',
                            paddingBottom: 'calc(0.125rem + var(--sab, 0px))',
                        }}
                    >
                        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-px bg-jw-line dark:block" />
                        <div
                            ref={tickerRef}
                            className={`relative flex items-center justify-center ${bandOpen ? 'will-change-transform' : ''}`}
                            style={{
                                opacity: isInitialized ? 1 : 0,
                                transition: 'opacity 0.3s ease-in-out',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                ref={animationRef}
                                className="flex items-center whitespace-nowrap"
                                style={{
                                    position: 'absolute',
                                    left: '5%',
                                    width: '100%',
                                    animation: bandOpen ? `ticker ${isMobile ? 25 / speed : 60 / speed}s linear infinite` : 'none',
                                    opacity: isFading ? 0.7 : 1,
                                    transition: `opacity ${TEXT_TRANSITION_DURATION / 3000}s ease-in-out`
                                }}
                            >
                                {Array.from({ length: 20 }).map((_, i) => {
                                    const displayText = (typeof currentText === 'string' && currentText.trim())
                                        ? currentText
                                        : fallbackMessage;

                                    return (
                                        <span
                                            key={i}
                                            className="text-xs font-semibold tracking-wide px-3 text-white/90 hover:text-white transition-colors"
                                        >
                                            {displayText}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="absolute right-2 top-1/2 z-10 -translate-y-1/2 transform p-1 text-white/80 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
            {!bandOpen && showButton && (
                <div className="fixed right-0 z-[60] p-2" style={{
                    right: `${SCROLLBAR_WIDTH + 4}px`,
                    bottom: 'calc(15px + env(safe-area-inset-bottom, 0px))'
                }}>
                    <button
                        onClick={toggleVisibility}
                        className={`bg-gradient-to-r from-purple-600 to-pink-500 text-white p-1 rounded-full shadow-lg hover:from-purple-700 hover:to-pink-600 transition-all ticker-show-btn ${TICKER_NIGHT_GRADIENT} dark:border dark:border-jw-line ${
                            buttonIsAnimatingOut ? 'animate-fade-out-blur' : 'animate-fade-in-blur'
                        }`}
                        title="Show ticker"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                    </button>
                </div>
            )}
        </>
    );
};

export default Ticker;
